import type {
	GridColumnProps,
	GridCustomCellProps,
} from "@tql/ui-components-react/kendo/grid";
import { Tooltip } from "@tql/ui-components-react/kendo/tooltip";
import { useMemo } from "react";

import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { ActionCell } from "~/shared/components/trax-grid/action-cell/action-cell.tsx";
import type { ShipmentLineItem } from "~/shared/schemas/shipment-management-schema";

import type { FreightGridRow } from "../../../types/freight-grid-row.ts";

const formatShipmentNumber = (value: string | null): string => {
	if (!value) {
		return "";
	}
	const parsed = Number.parseFloat(value);
	return Number.isNaN(parsed) ? "" : parsed.toLocaleString("en-US");
};

export const useGridColumns = ({
	isEditing,
	onEdit,
	onRemoveFromList,
}: {
	isEditing: boolean;
	onEdit?: (item: FreightGridRow) => void;
	onRemoveFromList?: (item: FreightGridRow) => void;
}): GridColumnProps[] => {
	const { data } = useQuoteDetailsPageLoadQueries();
	const unitTypes = data?.unitTypes;
	// The shipment only stores the unit as a code (e.g. "PLT"), so resolve the
	// full name (e.g. "Pallet") from reference data for display.
	const unitNameByCode = useMemo(() => {
		const map = new Map<string, string>();
		for (const type of unitTypes ?? []) {
			map.set(type.code.toUpperCase(), type.name);
		}
		return map;
	}, [unitTypes]);
	return useMemo(
		() => [
			...(isEditing
				? [
						{
							field: "",
							title: "",
							menu: false,
							show: true,
							width: "50px",
							cells: {
								data: (props: GridCustomCellProps) => (
									<ActionCell<FreightGridRow>
										{...props}
										onEdit={onEdit}
										onRemoveFromList={onRemoveFromList}
									/>
								),
							},
						},
					]
				: []),
			{
				field: "commodityDescription",
				title: "Description",
				menu: false,
				show: true,
				cells: {
					data: (props: GridCustomCellProps) => {
						const item = props.dataItem as ShipmentLineItem;
						const description = item.commodityDescription ?? "";
						return (
							<td {...props.tdProps}>
								<Tooltip
									anchorElement={"target"}
									openDelay={100}
									position={"top"}
								>
									<span className={"freight-desc-cell"} title={description}>
										{description}
									</span>
								</Tooltip>
							</td>
						);
					},
				},
			},
			{
				field: "handlingUnit",
				title: "Qty",
				menu: false,
				show: true,
				width: "50px",
				cells: {
					data: (props: GridCustomCellProps) => {
						const item = props.dataItem as ShipmentLineItem;
						return (
							<td {...props.tdProps}>
								{formatShipmentNumber(item.handlingUnit)}
							</td>
						);
					},
				},
			},
			{
				field: "unit",
				title: "Unit",
				menu: false,
				show: true,
				width: "80px",
				cells: {
					data: (props: GridCustomCellProps) => {
						const item = props.dataItem as ShipmentLineItem;
						const code = (item.unit ?? "").toUpperCase();
						return (
							<td {...props.tdProps}>
								{unitNameByCode.get(code) ?? item.unit ?? ""}
							</td>
						);
					},
				},
			},
			{
				field: "totalWeight",
				title: "Lbs",
				menu: false,
				show: true,
				cells: {
					data: (props: GridCustomCellProps) => {
						const item = props.dataItem as ShipmentLineItem;
						return (
							<td {...props.tdProps}>
								{formatShipmentNumber(item.totalWeight)}
							</td>
						);
					},
				},
				width: "50px",
			},
			{
				field: "",
				title: "L x W x H (in)",
				menu: false,
				show: true,
				cells: {
					data: (props: GridCustomCellProps) => {
						const item = props.dataItem as ShipmentLineItem;
						return (
							<td {...props.tdProps}>
								{formatShipmentNumber(item.length)} x{" "}
								{formatShipmentNumber(item.width)} x{" "}
								{formatShipmentNumber(item.height)}
							</td>
						);
					},
				},
				width: "120px",
			},
			{
				field: "class",
				title: "Class",
				menu: false,
				show: true,
				width: "50px",
			},
			{
				field: "nmfc",
				title: "NMFC",
				menu: false,
				show: true,
				width: "80px",
			},
			{
				field: "pieceCount",
				title: "Piece Count",
				menu: false,
				show: true,
				cells: {
					data: (props: GridCustomCellProps) => {
						const item = props.dataItem as ShipmentLineItem;
						return (
							<td {...props.tdProps}>
								{item.pieceCount == null
									? "N/A"
									: formatShipmentNumber(item.pieceCount)}
							</td>
						);
					},
				},
				width: "90px",
			},
			{
				field: "density",
				title: "Density",
				menu: false,
				show: true,
				cells: {
					data: (props: GridCustomCellProps) => {
						const item = props.dataItem as ShipmentLineItem;
						return (
							<td {...props.tdProps}>
								{item.density == null
									? ""
									: item.density.toLocaleString("en-US", {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
							</td>
						);
					},
				},
				width: "80px",
			},
			{
				field: "stackable",
				title: "Stackable",
				menu: false,
				show: true,
				width: "80px",
				cells: {
					data: (props: GridCustomCellProps) => {
						const item = props.dataItem as ShipmentLineItem;
						return <td {...props.tdProps}>{item.stackable ? "Yes" : "No"}</td>;
					},
				},
			},
			{
				field: "hazmat",
				title: "Hazmat",
				menu: false,
				show: true,
				width: "80px",
				cells: {
					data: (props: GridCustomCellProps) => {
						const item = props.dataItem as ShipmentLineItem;
						return <td {...props.tdProps}>{item.hazmat ? "Yes" : "No"}</td>;
					},
				},
			},
		],
		[isEditing, onEdit, onRemoveFromList, unitNameByCode],
	);
};
